// ----------------------------------------------------------------------
// Acceso a documentos (MediaBlob) servidos por el BACKEND.
//
// Regla arquitectonica: el binario SIEMPRE se pide al backend. El frontend nunca
// recibe ni construye una URL del almacen de objetos (MinIO) ni una URL prefirmada.
//
// Desde la migracion al almacen de objetos, `MediaBlob.File` (el byte[] que el API
// serializaba como base64 dentro de cada DTO anidado) queda en NULL para todo lo
// cargado despues del backfill. Por eso ya no se puede armar `data:image/...;base64,${x.file}`:
// hay que pedir el contenido al endpoint de streaming.
// ----------------------------------------------------------------------

/** Imagen que se pinta cuando el documento no existe, fue borrado o no hay permiso (404). */
export const MEDIA_PLACEHOLDER = '/assets/placeholder.svg';

/**
 * Extrae el identificador del documento de cualquiera de las formas en que el backend
 * lo devuelve hoy: `mediaId` (MediaBlob y MediaResponseDto), `MediaId` (serializacion
 * sin camelCase) o `id` en DTOs viejos. Devuelve null si no hay nada usable.
 */
export const mediaIdOf = (media) => {
  if (!media) return null;
  if (typeof media === 'string') return media;
  return media.mediaId ?? media.MediaId ?? media.id ?? null;
};

/**
 * Version del contenido: `uploadDate` cambia cada vez que se reemplaza el archivo bajo el
 * mismo mediaId. Va en la URL para que el navegador nunca sirva de su cache el anterior.
 */
export const mediaVersionOf = (media) =>
  media && typeof media === 'object' ? media.uploadDate ?? media.UploadDate ?? null : null;

/**
 * Ruta relativa (contra el baseURL de axios) del endpoint de streaming del backend.
 *
 * Cuando la vista esta en contexto de un paciente hay que pasar `patientId`: esa ruta
 * ancla el documento al paciente y el backend comprueba la pertenencia, asi que un
 * mediaId ajeno pedido desde una ruta legitima devuelve 404.
 *
 * @param {string|object} mediaId Identificador del documento o el objeto MediaResponseDto.
 * @param {object} [opts]
 * @param {string} [opts.patientId] Paciente dueno del documento, si la vista lo conoce.
 * @param {boolean} [opts.inline] true para visualizar en el navegador (PDF e imagenes).
 * @returns {string|null} Ruta relativa, o null si no hay mediaId.
 */
export const mediaContentPath = (mediaId, { patientId, inline = false } = {}) => {
  const id = mediaIdOf(mediaId);
  if (!id) return null;

  const base = patientId
    ? `/api/Media/patients/${patientId}/documents/${id}/content`
    : `/api/Media/${id}/content`;

  const query = new URLSearchParams();
  if (inline) query.set('inline', 'true');
  const version = mediaVersionOf(mediaId);
  if (version) query.set('v', String(version));

  const texto = query.toString();
  return texto ? `${base}?${texto}` : base;
};

/**
 * Adivina el content-type a partir del nombre del archivo. Solo para nombrar la descarga
 * o decidir el visor: el content-type autoritativo lo manda el backend en la respuesta.
 */
export const isPdfMedia = (media) => {
  if (!media) return false;
  const tipo = (media.contentType ?? media.fileType ?? '').toLowerCase();
  if (tipo.includes('pdf')) return true;
  return (media.fileName ?? '').toLowerCase().endsWith('.pdf');
};
